import type {
  Target,
  TargetPayload,
  TransformedTarget,
  TransformedTargetThreshold,
} from '../../../api/targets-client/targets-client.type';
import { TargetComparison } from '../../../api/targets-client/targets-client.type';
import { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { TableWidgetDataPoint, WidgetDataPoint } from '../../dashboards/hooks/dashboards.hooks.type';
import type { TargetForm, TargetFormObject } from './targets.type';
import { TargetThreshold } from './targets.type';

/**
 * Returns a formatted string representation of a target comparison
 *
 * @param {TargetComparison} comparison - The type of target comparison
 * @param {number} value - The target value
 * @param {number | null} upperValue - The upper target value for range comparisons
 * @returns {string} Formatted string representation of the comparison
 */
const getTargetComparisonSymbol = (comparison: TargetComparison, value: number, upperValue: number | null): string => {
  switch (comparison) {
    case TargetComparison.LT:
      return `<${value}`;
    case TargetComparison.LTE:
      return `≤${value}`;
    case TargetComparison.EQ:
      return `=${value}`;
    case TargetComparison.GTE:
      return `≥${value}`;
    case TargetComparison.GT:
      return `>${value}`;
    case TargetComparison.RANGE_IN:
      return `[${value}, ${upperValue}]`;
    case TargetComparison.RANGE_EX:
      return `(${value}, ${upperValue})`;
    default:
      return `=${value}`;
  }
};

/**
 * Returns a human-readable label for a target comparison type
 *
 * @param {TargetComparison} comparison - The type of target comparison
 * @returns {string} Human-readable label for the comparison type
 */
const getTargetComparisonLabel = (comparison: TargetComparison): string => {
  switch (comparison) {
    case TargetComparison.LT:
      return 'Less than (<)';
    case TargetComparison.LTE:
      return 'Less than or equal (≤)';
    case TargetComparison.EQ:
      return 'Equal (=)';
    case TargetComparison.GTE:
      return 'Greater than or equal (≥)';
    case TargetComparison.GT:
      return 'Greater than (>)';
    case TargetComparison.RANGE_IN:
      return 'Between (inclusive)';
    case TargetComparison.RANGE_EX:
      return 'Between (exclusive)';
    default:
      return 'Equal (=)';
  }
};

/**
 * Converts a Target object to a TargetForm object for use in forms
 *
 * @param {Target} target - The target object from the API
 * @returns {TargetForm} Form-friendly representation of the target
 */
const convertTargetToForm = (target: Target): TargetForm => {
  // Determine object type and objects based on which relationship array is populated
  let object_type: 'portfolio' | 'project' | 'board' | 'initiative' = 'board';
  let objects: TargetFormObject[] = [];

  if (target.portfolios?.length > 0) {
    object_type = 'portfolio';
    objects = target.portfolios.map((p) => ({ id: p.id, name: p.name }));
  } else if (target.projects?.length > 0) {
    object_type = 'project';
    objects = target.projects.map((p) => ({ id: p.id, name: p.name }));
  } else if (target.subprojects?.length > 0) {
    object_type = 'board';
    objects = target.subprojects.map((p) => ({ id: p.id, name: p.name }));
  } else if (target.initiatives?.length > 0) {
    object_type = 'initiative';
    objects = target.initiatives.map((i) => ({
      id: i.id,
      name: i.name,
      start_date: i.start_date,
      end_date: i.end_date,
    }));
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toISOString();
  };

  return {
    id: target.id,
    name: target.name || '',
    measure: target.measure || '',
    target_value: target.target_value,
    target_value_upper: target.target_value_upper,
    target_comparison: target.target_comparison,
    use_transform: target.use_transform,
    is_active: target.is_active ?? true,
    start_date: formatDate(target.start_date) ?? '',
    end_date: formatDate(target.end_date),
    time_allocation_type: target.time_allocation_type,
    is_work_period_target: target.is_work_period_target ?? false,
    object_type,
    objects,
    green_threshold_lower: target.green_threshold_lower ?? undefined,
    green_threshold_upper: target.green_threshold_upper ?? undefined,
    yellow_threshold_lower: target.yellow_threshold_lower ?? undefined,
    yellow_threshold_upper: target.yellow_threshold_upper ?? undefined,
    red_threshold_lower: target.red_threshold_lower ?? undefined,
    red_threshold_upper: target.red_threshold_upper ?? undefined,
  };
};

/**
 * Converts a TargetForm object to a TargetPayload object for API requests
 *
 * @param {TargetForm} form - The form data to convert
 * @returns {Partial<TargetPayload>} API-friendly representation of the target
 */
const convertFormToTargetPayload = (form: TargetForm): Partial<TargetPayload> => {
  const target: Partial<TargetPayload> = {
    id: form.id,
    name: form.name,
    measure: form.measure,
    target_value: form.target_value,
    target_value_upper: form.target_value_upper,
    target_comparison: form.target_comparison,
    is_active: form.is_active,
    start_date: form.start_date ?? undefined,
    end_date: form.end_date ?? undefined,
    time_allocation_type: form.time_allocation_type,
    is_work_period_target: form.is_work_period_target,
    use_transform: form.use_transform,
    portfolios: form.object_type === 'portfolio' ? form.objects.map((o) => o.id) : [],
    projects: form.object_type === 'project' ? form.objects.map((o) => o.id) : [],
    subprojects: form.object_type === 'board' ? form.objects.map((o) => o.id) : [],
    initiatives: form.object_type === 'initiative' ? form.objects.map((o) => o.id) : [],
    green_threshold_lower: form.green_threshold_lower ?? null,
    green_threshold_upper: form.green_threshold_upper ?? null,
    yellow_threshold_lower: form.yellow_threshold_lower ?? null,
    yellow_threshold_upper: form.yellow_threshold_upper ?? null,
    red_threshold_lower: form.red_threshold_lower ?? null,
    red_threshold_upper: form.red_threshold_upper ?? null,
  };
  return target;
};

/**
 * Builds a list of Mantine Combobox options with guaranteed non-null string labels.
 *
 * Mantine's default searchable-options filter calls `option.label.toLowerCase()`,
 * which throws — white-screening the whole modal — when an option label is
 * null/undefined. Target measures (`measure_title`) and objects (`name`) can both
 * come back null from the API, so this falls back to the value for any blank label
 * (null, undefined, or empty/whitespace) and drops any candidate missing a value.
 *
 * @param {Array<{ value: TValue | null | undefined; label: string | null | undefined }>} items - Raw option candidates
 * @returns {Array<{ value: TValue; label: string }>} Options safe to hand to a searchable Select/MultiSelect
 */
const buildComboboxOptions = <TValue extends string>(
  items: { value: TValue | null | undefined; label: string | null | undefined }[],
): { value: TValue; label: string }[] =>
  items
    // An option with no value is not meaningfully selectable. Dropping it means a
    // previously-saved selection whose value later comes back null would not render
    // in edit mode — acceptable here since it was never a valid choice to begin with.
    .filter((item): item is { value: TValue; label: string | null | undefined } => item.value != null)
    // Fall back to the value for any blank label (null, undefined, or empty/whitespace).
    // A null label is the production crash (Mantine calls label.toLowerCase()); an empty
    // label dodges the crash but renders an invisible, unsearchable row, so guard both.
    .map((item) => ({ value: item.value, label: item.label?.trim() ? item.label : item.value }));

/**
 * Determines the scope of a target (Sprint, Portfolio, Project, or Board)
 *
 * @param {Target} target - The target to determine the scope for
 * @returns {string} The scope of the target
 */
const getTargetScope = (target: Target): string => {
  if (target.is_work_period_target) return 'Sprint';
  if (target.portfolios?.length > 0) return 'Portfolio';
  if (target.projects?.length > 0) return 'Project';
  if (target.subprojects?.length > 0) return 'Board';
  if (target.initiatives?.length > 0) return 'Initiative';
  return '-';
};

/**
 * Returns a human-readable label for a time allocation type
 *
 * @param {TimeAllocationType | null} timeAllocationType - The time allocation type
 * @returns {string | null} Human-readable label for the time allocation type or null if not provided
 */
const getTimeAllocationTypeLabel = (timeAllocationType: TimeAllocationType | null): string | null => {
  switch (timeAllocationType) {
    case TimeAllocationType.Daily:
      return 'Daily';
    case TimeAllocationType.Weekly:
      return 'Weekly';
    case TimeAllocationType.BiWeekly:
      return 'Bi-Weekly';
    case TimeAllocationType.Monthly:
      return 'Monthly';
    case TimeAllocationType.Quarterly:
      return 'Quarterly';
    default:
      return null;
  }
};

/**
 * Returns the options for time allocation type based on the object type
 *
 * @param {string | null} objectType - The type of object (portfolio, project, board, initiative)
 * @returns {Array<{ value: TimeAllocationType; label: string }>} Array of options for time allocation type
 */
const getTimeAllocationTypeOptions = (objectType: string | null): { value: TimeAllocationType; label: string }[] => {
  const options = Object.values(TimeAllocationType)
    .filter((type) => {
      // If object type is initiative, only allow biweekly
      if (objectType === 'initiative') {
        return type === TimeAllocationType.BiWeekly;
      }
      return true;
    })
    .map((type) => ({
      value: type,
      label: getTimeAllocationTypeLabel(type),
    }));

  // buildComboboxOptions guards against an unlabeled enum value reaching Mantine's
  // Select filter as a null label (which throws on .toLowerCase()).
  return buildComboboxOptions(options);
};

/**
 * Determines the target threshold category for a given value based on defined threshold ranges.
 *
 * Evaluates thresholds in priority order: GREEN > YELLOW > RED. A value is considered to be
 * within a threshold if it falls within the bounds defined by the lower and upper limits.
 * If no bounds are defined for a threshold (both null), that threshold is skipped.
 *
 * @param {number} value - The value to evaluate against thresholds
 * @param {TransformedTargetThreshold} thresholds - Object containing threshold ranges with lower/upper bounds
 * @returns {TargetThreshold | null} The matching threshold category, or null if no thresholds match
 */
const getTargetThresholdForValue = (value: number, thresholds: TransformedTargetThreshold): TargetThreshold | null => {
  // Check thresholds in priority order: GREEN > YELLOW > RED
  const isWithinBounds = (value: number, lowerBound: number | null, upperBound: number | null): boolean => {
    const lower = lowerBound ?? -Infinity;
    const upper = upperBound ?? Infinity;
    return value >= lower && value <= upper;
  };
  if (
    (thresholds.green_threshold_lower !== null || thresholds.green_threshold_upper !== null) &&
    isWithinBounds(value, thresholds.green_threshold_lower, thresholds.green_threshold_upper)
  ) {
    return TargetThreshold.GREEN;
  } else if (
    (thresholds.yellow_threshold_lower !== null || thresholds.yellow_threshold_upper !== null) &&
    isWithinBounds(value, thresholds.yellow_threshold_lower, thresholds.yellow_threshold_upper)
  ) {
    return TargetThreshold.YELLOW;
  } else if (
    (thresholds.red_threshold_lower !== null || thresholds.red_threshold_upper !== null) &&
    isWithinBounds(value, thresholds.red_threshold_lower, thresholds.red_threshold_upper)
  ) {
    return TargetThreshold.RED;
  } else {
    return null;
  }
};

/**
 * Calculates the target thresholds for a given measure data
 *
 * @param {Record<string, number | null> | WidgetDataPoint[]} measureData - The measure data
 * @param {Record<string, TransformedTargetThreshold>} targetThresholds - The target thresholds
 * @param {TransformedTarget} selectedTarget - The selected target
 * @param {boolean} isWorkPeriodWidget - Whether the widget is a work period widget
 * @returns {Record<string, TargetThreshold | null>} The target thresholds
 */
const calculateTargetThresholds = (
  measureData: Record<string, number | null> | WidgetDataPoint[] | TableWidgetDataPoint[],
  targetThresholds: Record<string, TransformedTargetThreshold>,
  selectedTarget: TransformedTarget,
  isWorkPeriodWidget: boolean,
): Record<string, TargetThreshold | null> => {
  const result: Record<string, TargetThreshold | null> = {};

  let measureDataMap: Record<string, number | null>;
  if (Array.isArray(measureData)) {
    // Handle WidgetDataPoint[] case
    // Convert array to object format for processing
    measureDataMap = {};
    measureData.forEach((point) => {
      measureDataMap[point.date] = point.value;
    });
  } else {
    measureDataMap = measureData;
  }

  for (const key in measureDataMap) {
    const value = measureDataMap[key];
    const thresholds = isWorkPeriodWidget
      ? {
          green_threshold_lower: selectedTarget.green_threshold_lower,
          green_threshold_upper: selectedTarget.green_threshold_upper,
          yellow_threshold_lower: selectedTarget.yellow_threshold_lower,
          yellow_threshold_upper: selectedTarget.yellow_threshold_upper,
          red_threshold_lower: selectedTarget.red_threshold_lower,
          red_threshold_upper: selectedTarget.red_threshold_upper,
        }
      : targetThresholds[key];

    // Early exit for null/undefined cases
    if (value === null || !thresholds) {
      result[key] = null;
      continue;
    }

    result[key] = getTargetThresholdForValue(value, thresholds);
  }

  return result;
};

/**
 * Returns the appropriate stroke dash array for a target line based on the comparison type
 *
 * @param {string | undefined} comparison The target comparison type
 * @returns {string} Stroke dash array for the target line
 */
const getTargetStrokeDashArray = (comparison: string | undefined): string => {
  if (!comparison) return '0, 0';

  switch (comparison) {
    case TargetComparison.GT:
    case TargetComparison.LT:
    case TargetComparison.RANGE_EX:
      return '5 5';
    default:
      return '0, 0';
  }
};

/**
 * Returns the appropriate fill for a target area based on the comparison type
 *
 * @param {string} comparison The target comparison type
 * @param {string} color The base color for the fill
 * @returns {string | undefined} Fill configuration for the target area
 */
const getTargetFill = (comparison: string | undefined, color: string): string | undefined => {
  if (!comparison) return undefined;

  if (comparison === TargetComparison.EQ) return 'transparent';

  // Add opacity (33 = 20% opacity in hex)
  const fillColor = `${color}33`;

  return fillColor;
};

export {
  buildComboboxOptions,
  calculateTargetThresholds,
  convertFormToTargetPayload,
  convertTargetToForm,
  getTargetComparisonLabel,
  getTargetComparisonSymbol,
  getTargetFill,
  getTargetScope,
  getTargetStrokeDashArray,
  getTargetThresholdForValue,
  getTimeAllocationTypeLabel,
  getTimeAllocationTypeOptions,
};
